/**
 * ConnectionsPage
 *
 * Full-page view for managing user connections.
 * Features:
 * - Connections list with search/filter
 * - Pending requests management
 * - Connection actions (remove, block)
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Link2,
  Search,
  UserPlus,
  Users,
  Loader2,
  MoreVertical,
  UserMinus,
  Ban,
  ExternalLink,
  Clock,
  Lightbulb,
  ShieldOff,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useConnections,
  useConnectionCount,
  usePendingReceivedCount,
  useBlockedUsers,
  useBlockedUsersCount,
  useConnectionLoading,
  useConnectionActions,
} from '@/stores/connection';
import { UserSearchModal } from '@/components/connections/UserSearchModal';
import { PendingConnectionRequests } from '@/components/connections/PendingConnectionRequests';
import { ConnectionSuggestions } from '@/components/connections/ConnectionSuggestions';
import type { UserConnection, BlockedUser } from '@/types/connections';

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (connection: UserConnection): string => {
  const { user } = connection;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * Format connected date
 */
const formatConnectedDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * ConnectionCard - Individual connection card (Profile-style like suggestion cards)
 */
interface ConnectionCardProps {
  connection: UserConnection;
  onRemove: () => void;
  onBlock: () => void;
  onNavigate: () => void;
  isActioning: boolean;
}

const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  onRemove,
  onBlock,
  onNavigate,
  isActioning,
}) => {
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <div className="relative flex flex-col items-center p-4 rounded-lg border bg-card shadow-sm hover:shadow-md transition-all duration-200 group">
      {/* Actions dropdown - top right */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            disabled={isActioning}
          >
            {isActioning ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <MoreVertical className="h-3 w-3 text-muted-foreground" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onNavigate}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onRemove}
            className="text-destructive focus:text-destructive"
          >
            <UserMinus className="h-4 w-4 mr-2" />
            Remove
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onBlock}
            className="text-destructive focus:text-destructive"
          >
            <Ban className="h-4 w-4 mr-2" />
            Block
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Avatar - clickable */}
      <button onClick={onNavigate} className="mb-2">
        <Avatar className="h-14 w-14 ring-2 ring-background shadow hover:scale-105 transition-transform">
          <AvatarImage src={connection.user.avatar} alt={displayName} />
          <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>
      </button>

      {/* Name and username */}
      <button onClick={onNavigate} className="text-center mb-1">
        <h3 className="font-medium text-sm hover:text-primary transition-colors line-clamp-1">
          {displayName}
        </h3>
        <p className="text-xs text-muted-foreground">@{connection.user.username}</p>
      </button>

      {/* Connected date badge */}
      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 mb-2">
        <Clock className="h-2.5 w-2.5 mr-1" />
        {formatConnectedDate(connection.connectedAt)}
      </Badge>

      {/* View Profile button */}
      <Button
        variant="outline"
        size="sm"
        className="w-full mt-auto h-8 text-xs"
        onClick={onNavigate}
      >
        <ExternalLink className="h-3 w-3 mr-1" />
        View Profile
      </Button>
    </div>
  );
};

/**
 * ConnectionsPage - Main page component
 */
export const ConnectionsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const connections = useConnections();
  const connectionCount = useConnectionCount();
  const pendingReceivedCount = usePendingReceivedCount();
  const blockedUsers = useBlockedUsers();
  const blockedUsersCount = useBlockedUsersCount();
  const { isLoading } = useConnectionLoading();
  const { fetchConnections, fetchBlockedUsers, removeConnection, blockUser, unblockUser } = useConnectionActions();

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'remove' | 'block';
    connection: UserConnection;
  } | null>(null);
  const [unblockConfirm, setUnblockConfirm] = useState<BlockedUser | null>(null);

  // Get active tab from URL
  const activeTab = searchParams.get('tab') || 'connections';

  // Fetch connections on mount
  useEffect(() => {
    console.log('[ConnectionsPage] Mounting, fetching connections...');
    fetchConnections();
  }, [fetchConnections]);

  // Fetch blocked users when blocked tab is active
  useEffect(() => {
    if (activeTab === 'blocked') {
      console.log('[ConnectionsPage] Blocked tab active, fetching blocked users...');
      fetchBlockedUsers();
    }
  }, [activeTab, fetchBlockedUsers]);

  // Filter connections based on search query
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) {
      return connections;
    }

    const query = searchQuery.toLowerCase();
    return connections.filter((connection) => {
      const displayName = getDisplayName(connection).toLowerCase();
      const username = connection.user.username.toLowerCase();
      return displayName.includes(query) || username.includes(query);
    });
  }, [connections, searchQuery]);

  console.log('[ConnectionsPage] Rendering with', connectionCount, 'connections, tab:', activeTab);

  // Handle tab change
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // Handle remove connection
  const handleRemove = useCallback(async () => {
    if (!confirmAction || confirmAction.type !== 'remove') return;

    const { connection } = confirmAction;
    console.log('[ConnectionsPage] Removing connection:', connection.connectionId);

    setActioningId(connection.connectionId);
    setConfirmAction(null);

    try {
      await removeConnection(connection.connectionId);
    } finally {
      setActioningId(null);
    }
  }, [confirmAction, removeConnection]);

  // Handle block user
  const handleBlock = useCallback(async () => {
    if (!confirmAction || confirmAction.type !== 'block') return;

    const { connection } = confirmAction;
    console.log('[ConnectionsPage] Blocking user:', connection.user.id);

    setActioningId(connection.connectionId);
    setConfirmAction(null);

    try {
      await blockUser(connection.user.id);
    } finally {
      setActioningId(null);
    }
  }, [confirmAction, blockUser]);

  // Handle unblock user
  const handleUnblock = useCallback(async () => {
    if (!unblockConfirm) return;

    console.log('[ConnectionsPage] Unblocking user:', unblockConfirm.user.id);

    setActioningId(unblockConfirm.connectionId);
    setUnblockConfirm(null);

    try {
      await unblockUser(unblockConfirm.user.id);
    } finally {
      setActioningId(null);
    }
  }, [unblockConfirm, unblockUser]);

  // Handle navigate to user profile
  const handleNavigateToUser = useCallback((username: string) => {
    console.log('[ConnectionsPage] Navigating to user:', username);
    navigate(`/user/${username}`);
  }, [navigate]);

  return (
    <div className="container py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Link2 className="h-6 w-6" />
            Connections
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your network of connected users
          </p>
        </div>

        <Button
          onClick={() => {
            console.log('[ConnectionsPage] Opening search modal');
            setShowSearchModal(true);
          }}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Find Connections
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-6">
            <TabsTrigger value="connections" className="relative">
              <Users className="h-4 w-4 mr-2" />
              Connections
              {connectionCount > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1.5 text-xs">
                  {connectionCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="relative">
              <Clock className="h-4 w-4 mr-2" />
              Pending
              {pendingReceivedCount > 0 && (
                <Badge className="ml-2 h-5 min-w-[20px] px-1.5 text-xs bg-primary text-primary-foreground">
                  {pendingReceivedCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="relative">
              <Lightbulb className="h-4 w-4 mr-2" />
              Suggestions
            </TabsTrigger>
            <TabsTrigger value="blocked" className="relative">
              <Ban className="h-4 w-4 mr-2" />
              Blocked
              {blockedUsersCount > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1.5 text-xs">
                  {blockedUsersCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

        {/* Connections Tab */}
        <TabsContent value="connections">
          {/* Search input */}
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Connections grid */}
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex flex-col items-center p-4 rounded-lg border bg-card">
                  <div className="h-14 w-14 rounded-full bg-muted mb-2 animate-pulse" />
                  <div className="h-4 w-24 bg-muted rounded mb-1 animate-pulse" />
                  <div className="h-3 w-16 bg-muted rounded mb-2 animate-pulse" />
                  <div className="h-5 w-28 bg-muted rounded mb-2 animate-pulse" />
                  <div className="h-8 w-full bg-muted rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : connectionCount === 0 ? (
            <div className="text-center py-12">
              <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">No connections yet</h3>
              <p className="text-muted-foreground mb-4">
                Start building your network by finding and connecting with other users
              </p>
              <Button
                onClick={() => setShowSearchModal(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Find Connections
              </Button>
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                No connections match "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {filteredConnections.map((connection) => (
                <ConnectionCard
                  key={connection.connectionId}
                  connection={connection}
                  onRemove={() => setConfirmAction({ type: 'remove', connection })}
                  onBlock={() => setConfirmAction({ type: 'block', connection })}
                  onNavigate={() => handleNavigateToUser(connection.user.username)}
                  isActioning={actioningId === connection.connectionId}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <PendingConnectionRequests
            defaultTab="received"
          />
        </TabsContent>

        {/* Suggestions Tab - Full Width */}
        <TabsContent value="suggestions">
          <ConnectionSuggestions limit={10} compact={false} />
        </TabsContent>

        {/* Blocked Tab */}
        <TabsContent value="blocked">
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex flex-col items-center p-4 rounded-lg border bg-card">
                  <div className="h-14 w-14 rounded-full bg-muted mb-2 animate-pulse" />
                  <div className="h-4 w-24 bg-muted rounded mb-1 animate-pulse" />
                  <div className="h-3 w-16 bg-muted rounded mb-2 animate-pulse" />
                  <div className="h-5 w-28 bg-muted rounded mb-2 animate-pulse" />
                  <div className="h-8 w-full bg-muted rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : blockedUsersCount === 0 ? (
            <div className="text-center py-12">
              <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
                <Ban className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">No blocked users</h3>
              <p className="text-muted-foreground">
                Users you block will appear here. You can unblock them at any time.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {blockedUsers.map((blockedUser) => {
                const displayName = blockedUser.user.firstName && blockedUser.user.lastName
                  ? `${blockedUser.user.firstName} ${blockedUser.user.lastName}`
                  : blockedUser.user.firstName || blockedUser.user.username;
                const initials = getInitials(
                  blockedUser.user.firstName,
                  blockedUser.user.lastName,
                  blockedUser.user.username
                );
                const blockedDate = new Date(blockedUser.blockedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                });

                return (
                  <div
                    key={blockedUser.connectionId}
                    className="relative flex flex-col items-center p-4 rounded-lg border bg-card shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    {/* Avatar */}
                    <Avatar className="h-14 w-14 ring-2 ring-background shadow mb-2">
                      <AvatarImage src={blockedUser.user.avatar} alt={displayName} />
                      <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
                        {initials}
                      </AvatarFallback>
                    </Avatar>

                    {/* Name and username */}
                    <div className="text-center mb-1">
                      <h3 className="font-medium text-sm line-clamp-1">{displayName}</h3>
                      <p className="text-xs text-muted-foreground">@{blockedUser.user.username}</p>
                    </div>

                    {/* Blocked date badge */}
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 mb-2">
                      <Ban className="h-2.5 w-2.5 mr-1" />
                      {blockedDate}
                    </Badge>

                    {/* Unblock button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-auto h-8 text-xs"
                      onClick={() => setUnblockConfirm(blockedUser)}
                      disabled={actioningId === blockedUser.connectionId}
                    >
                      {actioningId === blockedUser.connectionId ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <ShieldOff className="h-3 w-3 mr-1" />
                          Unblock
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* User Search Modal */}
      <UserSearchModal
        open={showSearchModal}
        onOpenChange={setShowSearchModal}
      />

      {/* Confirmation Dialog */}
      <AlertDialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'remove'
                ? 'Remove Connection?'
                : 'Block User?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'remove' ? (
                <>
                  Are you sure you want to remove{' '}
                  <strong>{confirmAction && getDisplayName(confirmAction.connection)}</strong>{' '}
                  from your connections? They will need to send you a new connection request
                  to reconnect.
                </>
              ) : (
                <>
                  Are you sure you want to block{' '}
                  <strong>{confirmAction && getDisplayName(confirmAction.connection)}</strong>?
                  They will be removed from your connections and won't be able to send you
                  connection requests.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction?.type === 'remove' ? handleRemove : handleBlock}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {confirmAction?.type === 'remove' ? 'Remove' : 'Block'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unblock Confirmation Dialog */}
      <AlertDialog
        open={!!unblockConfirm}
        onOpenChange={(open) => !open && setUnblockConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unblock{' '}
              <strong>
                {unblockConfirm?.user.firstName && unblockConfirm?.user.lastName
                  ? `${unblockConfirm.user.firstName} ${unblockConfirm.user.lastName}`
                  : unblockConfirm?.user.firstName || unblockConfirm?.user.username}
              </strong>
              ? They will be able to send you connection requests again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={handleUnblock}>
              Unblock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConnectionsPage;
